import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import jobService from '../../utils/jobService';
import cacheService from '../../services/cacheService';
import { supabase } from '../../utils/supabase';
import { logError } from '../../utils/errorLogger';
import { getJobDetailsUrl, getJobApplicationUrl } from '../../utils/urlHelpers';
import { extractCompanyNameFromSlug, employerMatchesSlug } from '../../utils/companySlugUtils';
import { getConversationUrl } from '../../utils/conversationUrlHelpers';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import JobCard from './components/JobCard';
import JobCardSkeleton from '../../components/jobs/JobCardSkeleton';
import JobFilters from './components/JobFilters';
import Input from '../../components/ui/Input';
import VAHeaderNav from 'components/navigation/VAHeaderNav';
import EmployerHeaderNav from 'components/navigation/EmployerHeaderNav';

const JobSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, userProfile } = useAuth();
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redirectError, setRedirectError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    jobType: '',
    experienceLevel: '',
    skills: [],
    minSalary: '',
    maxSalary: '',
    sortBy: 'created_at',
    sortOrder: 'desc',
    employerId: ''
  });
  const [employerInfo, setEmployerInfo] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [isInitialized, setIsInitialized] = useState(false);
  const [isEmployerResolved, setIsEmployerResolved] = useState(false);

  // Refs to track current values for the browser navigation effect
  // This prevents the effect from reverting filters when it reads stale URL params
  const filtersRef = useRef(filters);
  const searchTermRef = useRef(searchTerm);
  filtersRef.current = filters;
  searchTermRef.current = searchTerm;

  const JOBS_PER_PAGE = 12;

  // Load user preferences from cache on mount
  useEffect(() => {
    const loadPreferences = async () => {
      if (user?.id && cacheService.isAvailable()) {
        try {
          const savedPrefs = await cacheService.getUserPreferences(user.id, 'job_search');
          if (savedPrefs && !searchParams.toString()) {
            // Only apply saved preferences if no URL params present
            setFilters(savedPrefs.filters || filters);
            setViewMode(savedPrefs.viewMode || 'grid');
            setShowFilters(savedPrefs.showFilters || false);
          }
        } catch (err) {
          logError('JobSearch.loadPreferences', err);
        }
      }
    };
    
    loadPreferences();
  }, [user?.id]); // Only run once when user is available

  // Save user preferences to cache when they change
  useEffect(() => {
    const savePreferences = async () => {
      if (user?.id && isInitialized && cacheService.isAvailable()) {
        try {
          const prefs = {
            filters,
            viewMode,
            showFilters,
            timestamp: Date.now()
          };
          await cacheService.setUserPreferences(user.id, 'job_search', prefs);
        } catch (err) {
          logError('JobSearch.savePreferences', err);
        }
      }
    };
    
    // Debounce the save operation
    const timer = setTimeout(savePreferences, 1000);
    return () => clearTimeout(timer);
  }, [filters, viewMode, showFilters, user?.id, isInitialized]);

  // Initialize from URL parameters on first load
  useEffect(() => {
    if (!isInitialized) {
      const initialSearchTerm = searchParams.get('search') || '';
      const initialFilters = {
        jobType: searchParams.get('jobType') || '',
        experienceLevel: searchParams.get('experienceLevel') || '',
        skills: searchParams.get('skills') ? searchParams.get('skills').split(',') : [],
        minSalary: searchParams.get('minSalary') || '',
        maxSalary: searchParams.get('maxSalary') || '',
        sortBy: searchParams.get('sortBy') || 'created_at',
        sortOrder: searchParams.get('sortOrder') || 'desc',
        employerId: '' // Will be set by fetchEmployerFromSlug if company slug present
      };
      
      setSearchTerm(initialSearchTerm);
      setDebouncedSearchTerm(initialSearchTerm);
      setFilters(initialFilters);
      setIsInitialized(true);
    }
  }, [isInitialized]); // Remove searchParams dependency to prevent re-initialization

  // Memoize company slug to avoid unnecessary re-computations
  const companySlug = useMemo(() => searchParams.get('company'), [searchParams]);
  
  // Track if we need to wait for employer resolution
  const needsEmployerResolution = useMemo(() => {
    return companySlug && !filters.employerId;
  }, [companySlug, filters.employerId]);

  // Function to update URL parameters using React Router's setSearchParams - optimized
  const updateURLParams = useCallback((newSearchTerm, newFilters) => {
    const params = new URLSearchParams();
    
    if (newSearchTerm) {
      params.set('search', newSearchTerm);
    }
    
    if (newFilters.jobType) params.set('jobType', newFilters.jobType);
    if (newFilters.experienceLevel) params.set('experienceLevel', newFilters.experienceLevel);
    if (newFilters.skills && newFilters.skills.length > 0) {
      params.set('skills', newFilters.skills.join(','));
    }
    if (newFilters.minSalary) params.set('minSalary', newFilters.minSalary);
    if (newFilters.maxSalary) params.set('maxSalary', newFilters.maxSalary);
    if (newFilters.sortBy && newFilters.sortBy !== 'created_at') {
      params.set('sortBy', newFilters.sortBy);
    }
    if (newFilters.sortOrder && newFilters.sortOrder !== 'desc') {
      params.set('sortOrder', newFilters.sortOrder);
    }
    // Handle company slug parameter
    if (companySlug) {
      params.set('company', companySlug);
    }
    
    // Use setSearchParams with replace: true to avoid navigation history entries
    setSearchParams(params, { replace: true });
  }, [setSearchParams, companySlug]);

  // Memoize search filters to prevent unnecessary re-computations
  const searchFilters = useMemo(() => ({
    ...filters,
    search: debouncedSearchTerm,
    userId: user?.id
  }), [filters, debouncedSearchTerm, user?.id]);

  // Optimized loadJobs function with memoization
  const loadJobs = useCallback(async (resetPage = false) => {
    try {
      setLoading(true);
      setError(null);

      const currentPage = resetPage ? 1 : page;
      const offset = (currentPage - 1) * JOBS_PER_PAGE;

      const queryFilters = {
        ...searchFilters,
        limit: JOBS_PER_PAGE,
        offset: offset
      };

      const result = await jobService.getJobs(queryFilters);

      if (!result?.success) {
        setError(result?.error || 'Failed to load jobs');
        return;
      }

      const newJobs = result.data || [];
      
      if (resetPage) {
        setJobs(newJobs);
        setPage(1);
      } else {
        setJobs(prev => [...prev, ...newJobs]);
      }

      setHasMore(newJobs.length === JOBS_PER_PAGE);
      
      if (!resetPage) {
        setPage(prev => prev + 1);
      }
    } catch (error) {
      setError('Something went wrong while loading jobs');
      logError('JobSearch.loadJobs', error);
    } finally {
      setLoading(false);
    }
  }, [page, searchFilters]);

  // Load jobs when key dependencies change - consolidated and optimized
  useEffect(() => {
    if (!isInitialized) return;
    
    // If company slug is present, wait for employer ID to be resolved
    if (companySlug && !filters.employerId && !isEmployerResolved) {
      return;
    }
    
    let isCancelled = false;
    
    const loadJobsDirectly = async () => {
      try {
        setLoading(true);
        setError(null);

        const queryFilters = {
          ...searchFilters,
          limit: JOBS_PER_PAGE,
          offset: 0 // Always reset to first page when search/filters change
        };

        const result = await jobService.getJobs(queryFilters);

        // Prevent state updates if component unmounted or new request started
        if (isCancelled) return;

        if (!result?.success) {
          setError(result?.error || 'Failed to load jobs');
          return;
        }

        const newJobs = result.data || [];
        setJobs(newJobs);
        setPage(1);
        setHasMore(newJobs.length === JOBS_PER_PAGE);
      } catch (error) {
        if (!isCancelled) {
          setError('Something went wrong while loading jobs');
          logError('JobSearch.loadJobsDirectly', error);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadJobsDirectly();
    
    // Cleanup function to prevent race conditions
    return () => {
      isCancelled = true;
    };
  }, [searchFilters, isInitialized, companySlug, filters.employerId, isEmployerResolved]);

  // Debounce search term (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Update URL when debounced search term or filters change
  useEffect(() => {
    if (isInitialized) {
      updateURLParams(debouncedSearchTerm, filters);
    }
  }, [debouncedSearchTerm, filters, updateURLParams, isInitialized]);
  
  // Check for error message from redirect
  useEffect(() => {
    if (location.state?.errorMessage && location.state?.fromJobDetails) {
      setRedirectError(location.state.errorMessage);
      
      // Clear the location state after displaying the message
      window.history.replaceState({}, document.title);
      
      // Clear the error message after 5 seconds
      const timer = setTimeout(() => {
        setRedirectError(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Fetch employer information when company slug is present - optimized with cache
  useEffect(() => {
    const fetchEmployerFromSlug = async () => {
      if (companySlug) {
        try {
          // Try to get from cache first
          const cacheKey = `employer_slug:${companySlug}`;
          if (cacheService.isAvailable()) {
            const cachedEmployer = await cacheService.get(cacheKey);
            if (cachedEmployer) {
              setEmployerInfo(cachedEmployer);
              setFilters(prev => ({ ...prev, employerId: cachedEmployer.id }));
              setIsEmployerResolved(true);
              return;
            }
          }

          // Get all employers and find the one that matches the slug
          const { data: employers, error } = await supabase
            .from('user_profiles')
            .select('id, full_name, company_name, profile_picture')
            .eq('role', 'employer');

          if (!error && employers) {
            const matchingEmployer = employers.find(emp => employerMatchesSlug(emp, companySlug));
            
            if (matchingEmployer) {
              // Cache the employer info for 30 minutes
              if (cacheService.isAvailable()) {
                await cacheService.set(cacheKey, matchingEmployer, 1800);
              }
              setEmployerInfo(matchingEmployer);
              setFilters(prev => ({ ...prev, employerId: matchingEmployer.id }));
              setIsEmployerResolved(true);
            } else {
              // Fallback: try to find by company name extracted from slug
              const companyName = extractCompanyNameFromSlug(companySlug);
              const nameMatch = employers.find(emp => 
                (emp.company_name && emp.company_name.toLowerCase() === companyName.toLowerCase()) ||
                (emp.full_name && emp.full_name.toLowerCase() === companyName.toLowerCase())
              );
              
              if (nameMatch) {
                // Cache the employer info
                if (cacheService.isAvailable()) {
                  await cacheService.set(cacheKey, nameMatch, 1800);
                }
                setEmployerInfo(nameMatch);
                setFilters(prev => ({ ...prev, employerId: nameMatch.id }));
                setIsEmployerResolved(true);
              } else {
                setIsEmployerResolved(true);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching employer from slug:', error);
          // Still mark as resolved so we don't block job loading forever
          setIsEmployerResolved(true);
        }
      } else {
        setEmployerInfo(null);
        setFilters(prev => ({ ...prev, employerId: '' }));
        setIsEmployerResolved(true);
      }
    };

    fetchEmployerFromSlug();
  }, [companySlug]);

  // Handle browser navigation (back/forward buttons)
  // Only depends on searchParams and isInitialized — uses refs for comparison
  // to avoid reverting filter state when URL hasn't been updated yet
  useEffect(() => {
    if (!isInitialized) return; // Skip during initial load
    
    const newSearchTerm = searchParams.get('search') || '';
    const newFilters = {
      jobType: searchParams.get('jobType') || '',
      experienceLevel: searchParams.get('experienceLevel') || '',
      skills: searchParams.get('skills') ? searchParams.get('skills').split(',') : [],
      minSalary: searchParams.get('minSalary') || '',
      maxSalary: searchParams.get('maxSalary') || '',
      sortBy: searchParams.get('sortBy') || 'created_at',
      sortOrder: searchParams.get('sortOrder') || 'desc',
      // Preserve employerId if company slug is present (it will be resolved by fetchEmployerFromSlug)
      employerId: searchParams.get('company') ? filtersRef.current.employerId : ''
    };
    
    // Only update if the URL search term is different from current search term
    if (newSearchTerm !== searchTermRef.current) {
      setSearchTerm(newSearchTerm);
    }
    
    // Check if filters have changed compared to current state (via ref)
    const filtersChanged = JSON.stringify(newFilters) !== JSON.stringify(filtersRef.current);
    if (filtersChanged) {
      setFilters(newFilters);
    }
  }, [searchParams, isInitialized]);

  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
    // URL will be updated by the debounce effect
  }, []);

  const handleFilterChange = useCallback((newFilters) => {
    // Log filters in development mode for debugging
    if (import.meta.env.DEV) {
      console.log('Applied filters:', newFilters);
    }
    setFilters(newFilters);
    // URL will be updated by the debounce effect
  }, []);

  const handleJobClick = useCallback((job) => {
    navigate(getJobDetailsUrl(job));
  }, [navigate]);

  const handleApply = useCallback((job) => {
    if (!user) {
      // If public user, redirect to login with return URL to application
      navigate('/login-register?redirect=' + encodeURIComponent(getJobApplicationUrl(job)));
      return;
    }
    navigate(getJobApplicationUrl(job));
  }, [user, navigate]);

  const handleBookmark = async (jobId, _isCurrentlyBookmarked) => {
    if (!user) {
      // If public user, redirect to login with return URL to job search
      navigate('/login-register?redirect=/job-search');
      return;
    }

    try {
      const result = await jobService.toggleBookmark(user.id, jobId);
      
      if (result?.success) {
        // Update local state to reflect bookmark change
        setJobs(prev => prev.map(job => 
          job.id === jobId 
            ? { ...job, isBookmarked: result.bookmarked }
            : job
        ));
      } else {
        logError('JobSearch.toggleBookmark', result?.error);
      }
    } catch (error) {
      logError('JobSearch.toggleBookmark', error);
    }
  };

  const handleContactEmployer = async (job) => {
    if (!user) {
      navigate('/login-register?redirect=' + encodeURIComponent(window.location.pathname));
      return;
    }
    
    try {
      let employerData;
      
      // If we don't have complete employer data, fetch it first
      if (!job.employer && job.employer_id) {
        const { data: employer, error } = await supabase
          .from('user_profiles')
          .select('id, full_name, company_name, role, profile_picture')
          .eq('id', job.employer_id)
          .single();
          
        if (error) throw error;
        employerData = employer;
      } else {
        // Use available employer data
        employerData = job.employer_profile || job.employer || { 
          id: job.employer_id, 
          full_name: job.company_name || "Employer",
          company_name: job.company_name || "Company"
        };
      }
      
      // Ensure we have a valid ID
      if (!employerData.id && job.employer_id) {
        employerData.id = job.employer_id;
      }
      
      // Use secure conversation URL
      const conversationUrl = getConversationUrl(employerData, job.id);
      navigate(conversationUrl);
    } catch (error) {
      console.error('Error generating secure conversation URL:', error);
      // Fallback to traditional messaging if URL generation fails
      navigate('/messages');
    }
  };

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      loadJobs(false);
    }
  }, [loading, hasMore, loadJobs]);

  // Show loading state for initial load
  if (loading && jobs.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        {userProfile?.role === 'employer' ? <EmployerHeaderNav /> : <VAHeaderNav />}

        {/* Skeleton search bar */}
        <div className="bg-surface border-b border-border sticky top-28 z-10">
          <div className="content-container mx-auto px-4 py-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="w-full md:w-auto flex-1 flex items-center gap-2">
                <div className="relative flex-1 justify-self-end">
                  <div className="w-full md:w-96 h-10 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg animate-pulse"></div>
                </div>
                <div className="h-10 w-20 bg-gradient-to-r from-gray-200 to-gray-300 rounded animate-pulse"></div>
              </div>
              <div className="flex items-center gap-2 w-full md:w-auto justify-end">
                <div className="h-8 w-16 bg-gradient-to-r from-gray-200 to-gray-300 rounded animate-pulse lg:hidden"></div>
              </div>
            </div>
          </div>
        </div>

        <main className="pt-4">
          <div className="content-container mx-auto px-4 py-8">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Filters Sidebar Skeleton */}
              <div className="lg:w-80 hidden lg:block">
                <div className="sticky top-24">
                  <div className="bg-surface rounded-lg border border-border p-6 animate-pulse">
                    <div className="h-6 w-20 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-4"></div>
                    <div className="space-y-4">
                      {[1, 2, 3, 4].map((index) => (
                        <div key={index}>
                          <div className="h-4 w-24 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2"></div>
                          <div className="h-10 w-full bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Jobs Grid Skeleton */}
              <div className="flex-1">
                {/* Results Header Skeleton */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <div>
                    <div className="h-8 w-48 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2 animate-pulse"></div>
                    <div className="h-4 w-32 bg-gradient-to-r from-gray-200 to-gray-300 rounded animate-pulse"></div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="h-10 w-32 bg-gradient-to-r from-gray-200 to-gray-300 rounded animate-pulse"></div>
                    <div className="h-10 w-24 bg-gradient-to-r from-gray-200 to-gray-300 rounded animate-pulse"></div>
                    <div className="h-10 w-16 bg-gradient-to-r from-gray-200 to-gray-300 rounded animate-pulse"></div>
                  </div>
                </div>

                {/* Skeleton Job Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-6">
                  {Array.from({ length: JOBS_PER_PAGE }).map((_, index) => (
                    <JobCardSkeleton key={`skeleton-${index}`} delay={index * 100} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation is handled by NavigationLayout */}
      
      {/* Display redirect error message if present */}
      {redirectError && (
        <div className="bg-error-50 border-l-4 border-error p-4 my-2 mx-4">
          <div className="flex items-center">
            <Icon name="AlertCircle" size={20} className="text-error mr-3" />
            <p className="text-error-700">{redirectError}</p>
          </div>
        </div>
      )}
      {userProfile?.role === 'employer' ? <EmployerHeaderNav /> : <VAHeaderNav />}
      <div className="bg-surface border-b border-border sticky top-28 z-10">
        <div className="content-container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <form 
              onSubmit={(e) => {
                e.preventDefault();
                handleSearch(searchTerm);
              }} 
              className="w-full md:w-auto flex-1 flex items-center gap-2"
            >
              <div className="relative flex-1 justify-self-end">
                <Input
                  type="text"
                  placeholder="Search jobs, skills, companies..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  className="w-full md:w-96 pl-10"
                />
                <Icon 
                  name="Search" 
                  size={18} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" 
                />
              </div>
              <Button type="submit" variant="primary" iconName="Search">
                Search
              </Button>
            </form>
            
            <div className="flex items-center gap-2 w-full md:w-auto justify-end">
              {user && (
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Bookmark"
                  onClick={() => navigate('/saved-jobs')}
                  className="sm:hidden"
                  title="View saved jobs"
                >
                  Favorites
                </Button>
              )}
              <Button
                variant={showFilters ? "primary" : "outline"}
                size="sm"
                iconName="Filter"
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden"
              >
                Filters
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="pt-4">
        <div className="content-container mx-auto px-4 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters Sidebar */}
            <div className={`lg:w-80 ${showFilters ? 'block' : 'hidden lg:block'}`}>
              <div className="sticky top-24">
                <JobFilters
                  filters={filters}
                  onFiltersChange={handleFilterChange}
                  resultsCount={jobs.length}
                />
              </div>
            </div>

            {/* Jobs Grid */}
            <div className="flex-1">
              {/* Results Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-text-primary mb-1">
                    {searchTerm ? `Search results for "${searchTerm}"` : 
                     employerInfo ? `Jobs at ${employerInfo.company_name || employerInfo.full_name}` : 
                     searchParams.get('company') ? `Jobs at ${extractCompanyNameFromSlug(searchParams.get('company'))}` :
                     'Browse Jobs'}
                  </h1>
                  {employerInfo && (
                    <div className="flex items-center gap-2 mb-2">
                      {employerInfo.profile_picture ? (
                        <img 
                          src={employerInfo.profile_picture} 
                          alt={employerInfo.company_name || employerInfo.full_name}
                          className="h-6 w-6 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-6 w-6 bg-background rounded-full flex items-center justify-center">
                          <Icon name="Briefcase" size={12} className="text-text-muted" />
                        </div>
                      )}
                      <span className="text-text-primary font-medium">
                        {employerInfo.company_name || employerInfo.full_name}
                      </span>
                      <button 
                        className="text-sm text-primary hover:text-primary-dark flex items-center"
                        onClick={() => {
                          const newParams = new URLSearchParams(searchParams);
                          newParams.delete('company');
                          setSearchParams(newParams, { replace: true });
                        }}
                      >
                        <Icon name="X" size={14} className="mr-1" />
                        Clear filter
                      </button>
                    </div>
                  )}
                 
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-text-secondary">Sort by:</span>
                    <select
                      value={`${filters.sortBy}-${filters.sortOrder}`}
                      onChange={(e) => {
                        const [sortBy, sortOrder] = e.target.value.split('-');
                        handleFilterChange({ ...filters, sortBy, sortOrder });
                      }}
                      className="px-3 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                    >
                      <option value="created_at-desc">Most Recent</option>
                      <option value="created_at-asc">Oldest First</option>
                      <option value="title-asc">Title A-Z</option>
                      <option value="title-desc">Title Z-A</option>
                      <option value="min_salary-desc">Highest Budget</option>
                      <option value="min_salary-asc">Lowest Budget</option>
                      <option value="hourly_rate_min-desc">Highest Hourly Rate</option>
                      <option value="hourly_rate_min-asc">Lowest Hourly Rate</option>
                    </select>
                  </div>
                  
                  {user && (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Bookmark"
                      onClick={() => navigate('/saved-jobs')}
                      className="hidden sm:flex"
                    >
                      Bookmark
                    </Button>
                  )}
                  
                  <div className="flex items-center border border-border rounded-lg overflow-hidden">
                    <button
                      className={`p-2 ${viewMode === 'grid' ? 'bg-primary text-white' : 'bg-surface text-text-secondary hover:bg-background'}`}
                      onClick={() => setViewMode('grid')}
                      title="Grid view"
                    >
                      <Icon name="Grid" size={18} />
                    </button>
                    <button
                      className={`p-2 ${viewMode === 'list' ? 'bg-primary text-white' : 'bg-surface text-text-secondary hover:bg-background'}`}
                      onClick={() => setViewMode('list')}
                      title="List view"
                    >
                      <Icon name="List" size={18} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Error State */}
              {error && (
                <div className="bg-error-50 border border-error-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <Icon name="AlertCircle" size={20} className="text-error mr-3" />
                    <div>
                      <p className="text-error font-medium">Error loading jobs</p>
                      <p className="text-error-600 text-sm">{error}</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadJobs(true)}
                    className="mt-3"
                  >
                    Try Again
                  </Button>
                </div>
              )}

              {/* Jobs Grid or List */}
              {jobs.length > 0 ? (
                <div className="space-y-4">
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-6">
                      {jobs.map((job) => (
                        <JobCard
                          key={job.id}
                          job={job}
                          onViewDetails={handleJobClick}
                          onApply={handleApply}
                          onBookmark={handleBookmark}
                          onContactEmployer={handleContactEmployer}
                          userProfile={userProfile}
                        />
                      ))}
                      
                      {/* Show skeleton cards when loading more */}
                      {loading && jobs.length > 0 && (
                        Array.from({ length: 6 }).map((_, index) => (
                          <JobCardSkeleton 
                            key={`loading-skeleton-${index}`} 
                            delay={index * 100} 
                            viewMode="grid"
                          />
                        ))
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {jobs.map((job) => (
                        <div 
                          key={job.id}
                          className="bg-surface rounded-lg border border-border hover:border-primary transition-colors overflow-hidden cursor-pointer"
                          onClick={() => handleJobClick(job)}
                        >
                          <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start gap-3">
                                {job.employer?.profile_picture ? (
                                  <img
                                    src={job.employer.profile_picture}
                                    alt={job.employer.company_name || 'Company logo'}
                                    className="h-12 w-12 rounded object-cover"
                                  />
                                ) : (
                                  <div className="h-12 w-12 bg-background rounded flex items-center justify-center">
                                    <Icon name="Briefcase" size={24} className="text-text-muted" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-lg font-semibold text-text-primary truncate">{job.title}</h3>
                                  <p className="text-text-secondary truncate">
                                    {job.company_name || job.employer?.company_name || job.employer?.full_name || 'Unknown Company'}
                                  </p>
                                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                                    <span className="flex items-center text-sm text-text-muted">
                                      <Icon name="MapPin" size={14} className="mr-1" /> 
                                      {job.is_remote ? 'Remote' : job.location}
                                    </span>
                                    {job.job_type && (
                                      <span className="flex items-center text-sm text-text-muted">
                                        <Icon name="Clock" size={14} className="mr-1" /> 
                                        {job.job_type.replace('_', ' ')}
                                      </span>
                                    )}
                                    {job.budget_min && job.budget_max && (
                                      <span className="flex items-center text-sm text-text-muted">
                                        <Icon name="DollarSign" size={14} className="mr-1" /> 
                                        ${job.budget_min.toLocaleString()} - ${job.budget_max.toLocaleString()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {job.required_skills && job.required_skills.length > 0 && (
                                <div className="mt-4 flex flex-wrap gap-2">
                                  {job.required_skills.slice(0, 3).map((skill) => (
                                    <span 
                                      key={skill} 
                                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700"
                                    >
                                      {typeof skill === 'object' && skill !== null ? (skill.name || skill.skill || 'Unknown') : skill}
                                    </span>
                                  ))}
                                  {job.required_skills.length > 3 && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-background text-text-secondary">
                                      +{job.required_skills.length - 3} more
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex md:flex-col items-center gap-3 mt-4 md:mt-0">
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleApply(job);
                                }}
                              >
                                Apply Now
                              </Button>
                              <button
                                className="p-2 rounded-full hover:bg-background transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBookmark(job.id, job.isBookmarked);
                                }}
                                title={job.isBookmarked ? "Remove bookmark" : "Bookmark job"}
                              >
                                <Icon 
                                  name={job.isBookmarked ? "Bookmark" : "BookmarkPlus"} 
                                  size={20} 
                                  className={job.isBookmarked ? "text-primary" : "text-text-muted"} 
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {/* Show skeleton cards when loading more */}
                      {loading && jobs.length > 0 && (
                        Array.from({ length: 6 }).map((_, index) => (
                          <JobCardSkeleton 
                            key={`loading-skeleton-list-${index}`} 
                            delay={index * 100} 
                            viewMode="list"
                          />
                        ))
                      )}
                    </div>
                  )}

                  {/* Load More Button */}
                  {hasMore && (
                    <div className="flex justify-center mt-8">
                      <Button
                        variant="outline"
                        onClick={loadMore}
                        disabled={loading}
                        iconName={loading ? "Loader2" : "Plus"}
                        className={loading ? "animate-pulse" : ""}
                      >
                        {loading ? 'Loading more jobs...' : 'Load More Jobs'}
                      </Button>
                    </div>
                  )}
                </div>
              ) : !loading && (
                // Empty State
                <div className="text-center py-12">
                  <Icon name="Briefcase" size={64} className="text-text-muted mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-text-primary mb-2">
                    No jobs found
                  </h3>
                  <p className="text-text-secondary mb-6 max-w-md mx-auto">
                    {searchTerm || Object.values(filters).some(f => f && f !== '' && (!Array.isArray(f) || f.length > 0))
                      ? "No jobs match your current search criteria. Try adjusting your filters or search terms." :"No job opportunities are available at the moment. Check back later for new postings."
                    }
                  </p>
                  
                  {(searchTerm || Object.values(filters).some(f => f && f !== '' && (!Array.isArray(f) || f.length > 0))) && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        const clearedFilters = {
                          jobType: '',
                          experienceLevel: '',
                          skills: [],
                          minSalary: '',
                          maxSalary: '',
                          sortBy: 'created_at',
                          sortOrder: 'desc'
                        };
                        setSearchTerm('');
                        setFilters(clearedFilters);
                        // URL will be updated by the debounce effect
                      }}
                    >
                      Clear All Filters
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
          
          
        </div>
      </main>

      {/* Preview Mode Banner for Unauthenticated Users */}
      {!user && (
        <div className="fixed bottom-4 left-4 right-4 z-40">
          <div className="bg-primary text-white p-4 rounded-lg shadow-lg max-w-md mx-auto">
            <div className="flex items-center justify-between">
              <div className='w-3/4'>
                <p className="font-medium">Public Browsing Mode</p>
                <p className="text-sm opacity-90">Sign in to apply to jobs, save favorites, and access more features</p>
              </div>
              <Button
                variant="white"
                size="sm"
                onClick={() => navigate('/login-register?redirect=' + encodeURIComponent(window.location.pathname))}
              >
                Sign In
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobSearch;

